import React, { useEffect, useState } from 'react';
import { fetchWallMotion, WallMotionResult, MOTION_LEGEND, MotionInfo } from '../wallMotion';

/**
 * Wall-Motion segment bullseye — renders the AHA 17-segment LV model coloured by
 * the per-segment wall-motion score read from the study's DICOM-SR (real data).
 * Self-contained (inline styles); fetches via fetchWallMotion(studyInstanceUID).
 */

const C = 190; // svg center
const R = { apex: 42, apical: 90, mid: 138, basal: 186 };
const NO_DATA_COLOR = '#1c2c44';

interface Seg { id: string; ring: 'basal' | 'mid' | 'apical' | 'apex'; a0: number; a1: number; abbr: string; }

// clockwise from anterior (top, -90°)
const WALLS6: [string, number, string][] = [
  ['anterior', -120, 'A'],
  ['anterolateral', -60, 'AL'],
  ['inferolateral', 0, 'IL'],
  ['inferior', 60, 'I'],
  ['inferoseptal', 120, 'IS'],
  ['anteroseptal', 180, 'AS'],
];
const APICAL4: [string, number, number, string][] = [
  ['anterior', -135, -45, 'A'],
  ['lateral', -45, 45, 'L'],
  ['inferior', 45, 135, 'I'],
  ['septal', 135, 225, 'S'],
];

const SEGMENTS: Seg[] = [
  ...WALLS6.map(([w, a0]) => ({ id: `basal ${w}`, ring: 'basal' as const, a0, a1: a0 + 60, abbr: WALLS6.find(x => x[0] === w)![2] })),
  ...WALLS6.map(([w, a0]) => ({ id: `mid ${w}`, ring: 'mid' as const, a0, a1: a0 + 60, abbr: WALLS6.find(x => x[0] === w)![2] })),
  ...APICAL4.map(([w, a0, a1, ab]) => ({ id: `apical ${w}`, ring: 'apical' as const, a0, a1, abbr: ab })),
];

const rad = (d: number) => (d * Math.PI) / 180;
const pt = (r: number, a: number) => [C + r * Math.cos(rad(a)), C + r * Math.sin(rad(a))];

function sectorPath(ri: number, ro: number, a0: number, a1: number) {
  const [x0o, y0o] = pt(ro, a0);
  const [x1o, y1o] = pt(ro, a1);
  const [x1i, y1i] = pt(ri, a1);
  const [x0i, y0i] = pt(ri, a0);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M${x0o},${y0o} A${ro},${ro} 0 ${large} 1 ${x1o},${y1o} L${x1i},${y1i} A${ri},${ri} 0 ${large} 0 ${x0i},${y0i} Z`;
}
const ringR = (ring: Seg['ring']) =>
  ring === 'basal' ? [R.mid, R.basal] : ring === 'mid' ? [R.apical, R.mid] : [R.apex, R.apical];

const WallMotionBullseye: React.FC<{ studyInstanceUID?: string }> = ({ studyInstanceUID }) => {
  const [data, setData] = useState<WallMotionResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!studyInstanceUID) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setError('');
    fetchWallMotion(studyInstanceUID)
      .then(r => { if (!cancelled) setData(r); })
      .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load wall motion'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studyInstanceUID]);

  const wrap: React.CSSProperties = { color: '#e9f0f8', fontFamily: "'Hanken Grotesk', system-ui, sans-serif", padding: 16 };
  const panel: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' };

  if (loading) return <div style={{ ...wrap, color: '#9fb2cc' }}>Loading wall-motion from DICOM-SR…</div>;
  if (error) return <div style={{ ...wrap, color: '#fb7185' }}>{error}</div>;
  if (!data || data.scoredCount === 0)
    return <div style={{ ...wrap, color: '#9fb2cc' }}>No wall-motion scoring found in this study's structured report.</div>;

  const segOf = (id: string): MotionInfo | undefined => data.segments[id];
  const abnormal = SEGMENTS.map(s => ({ s, m: segOf(s.id) })).filter(x => x.m && x.m.score > 1);

  return (
    <div style={wrap}>
      <div style={panel}>
        {/* bullseye */}
        <svg viewBox="0 0 380 380" width={360} height={360} style={{ flexShrink: 0 }}>
          {SEGMENTS.map(seg => {
            const [ri, ro] = ringR(seg.ring);
            const m = segOf(seg.id);
            const fill = m ? m.color : NO_DATA_COLOR;
            const [lx, ly] = pt((ri + ro) / 2, (seg.a0 + seg.a1) / 2);
            return (
              <g key={seg.id}>
                <path d={sectorPath(ri, ro, seg.a0, seg.a1)} fill={fill} stroke="#0a1626" strokeWidth={2}>
                  <title>{`${seg.id} — ${m ? m.label : 'not scored'}`}</title>
                </path>
                <text x={lx} y={ly} fill="#04140f" fontSize={11} fontWeight={700} textAnchor="middle" dominantBaseline="middle" style={{ pointerEvents: 'none' }}>
                  {seg.abbr}
                </text>
              </g>
            );
          })}
          {/* apex cap (17th) — usually not scored in 16-seg SR */}
          <circle cx={C} cy={C} r={R.apex} fill={segOf('apex')?.color || NO_DATA_COLOR} stroke="#0a1626" strokeWidth={2} />
        </svg>

        {/* side panel */}
        <div style={{ minWidth: 220, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#2dd4bf' }}>
            Left Ventricle
          </div>
          <div style={{ fontFamily: "'Spectral', serif", fontSize: 22, fontWeight: 700, margin: '2px 0 4px' }}>
            Wall Motion
          </div>
          <div style={{ fontSize: 13, color: '#9fb2cc', marginBottom: 16 }}>
            WMSI <b style={{ color: '#fff', fontSize: 16 }}>{data.wmsi ?? '—'}</b>
            <span style={{ marginLeft: 12 }}>{data.scoredCount} segments scored</span>
          </div>

          {/* legend */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {MOTION_LEGEND.map(l => (
              <div key={l.score} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: '#9fb2cc' }}>
                <span style={{ width: 13, height: 13, borderRadius: 4, background: l.color, flexShrink: 0 }} />
                {l.score} · {l.label}
              </div>
            ))}
          </div>

          {/* abnormal list */}
          {abnormal.length > 0 ? (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6a7e9c', marginBottom: 8 }}>
                Abnormal segments
              </div>
              {abnormal.map(({ s, m }) => (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '6px 0', borderBottom: '1px solid #243652' }}>
                  <span style={{ textTransform: 'capitalize' }}>{s.id}</span>
                  <span style={{ color: m!.color, fontWeight: 600 }}>{m!.label}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#4ade80', fontWeight: 600 }}>All scored segments normal.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WallMotionBullseye;
